import {
    PermissionSchema,
    RoleSchema,
    TAssignAdminRolePayload,
    TAssignAdminRoleResponse,
    TGetAdminRolePermissionsResponse,
    TGetAdminRoleResponse,
    TGetRolesOfUserResponse,
    TGetUserRoleSchema,
    TUpdateAdminRolePayload,
    TUpdateAdminRoleResponse,
    TGetAdminRolesParams,
    // Role-Permission types
    RolePermissionSchema,
    TAssignRolePermissionPayload,
    TAssignRolePermissionResponse,
    TUpdateRolePermissionPayload,
    TUpdateRolePermissionResponse,
    TGetRolePermissionsResponse,
} from '@/types/admin-management/admin-roles-types';
import {
    TAdminUsers,
    TGetAdminsParams,
    TGetAdminsResponse,
} from '../types/admin-management/admin-types';
import { constructUrl } from '../utils/construct-url';
import { getParams } from '../utils/get-param-util';
import { api } from './authService';

import { AdminManagement } from './endpoints';

export const getAdmins = async (
    param: TGetAdminsParams
): Promise<TGetAdminsResponse> => {
    const params = getParams<Partial<any>>(param);
    const url = constructUrl(`${AdminManagement.getAdmins}`, params);
    try {
        const response = await api.get<TGetAdminsResponse>(url);

        return response.data;
    } catch (error: any) {
        // throw transformErrorResponse(error);
    }
};

export const getAdminById = async (id): Promise<TAdminUsers> => {
    try {
        const response = await api.get<TAdminUsers>(
            AdminManagement.getAdminById(id)
        );
        return response.data;
    } catch (err: any) {
        // throw transformErrorResponse(error);
    }
};

export const updateAdminDetails = async (
    id: string,
    status: string
): Promise<any> => {
    try {
        const response = await api.patch<any>(
            AdminManagement.updateAdminDetails(id),
            { status }
        );
        return response.data;
    } catch (error: any) {
        // throw transformErrorResponse(error);
    }
};

export const updateAdminRole = async (
    payload: TUpdateAdminRolePayload & { id: string }
): Promise<TUpdateAdminRoleResponse> => {
    const { id, ...rest } = payload;
    try {
        const response = await api.patch<TUpdateAdminRoleResponse>(
            AdminManagement.updateAdminRole(id),
            rest
        );
        return response.data;
    } catch (error: any) {
        // throw transformErrorResponse(error);
    }
};

export const removeAdminRole = async (roleId: string): Promise<unknown> => {
    // removeAdminRole
    try {
        const response = await api.delete(
            AdminManagement.removeAdminRole(roleId)
        );
        return response.data;
    } catch (error: any) {
        // throw transformErrorResponse(error);
    }
};

export const assignAdminRole = async (
    payload: TAssignAdminRolePayload
): Promise<TAssignAdminRoleResponse> => {
    assignAdminRole;
    try {
        const response = await api.post<TAssignAdminRolePayload>(
            AdminManagement.assignAdminRole,
            payload
        );
        return response.data;
    } catch (error: any) {
        // throw transformErrorResponse(error);
    }
};

export const getAdminRoles = async (params: TGetAdminRolesParams = {}): Promise<TGetAdminRoleResponse> => {
    try {
        // Always include platform=back-office for admin portal
        const queryParams = new URLSearchParams({
            platform: 'back-office',
            ...params
        });

        const response = await api.get<TGetAdminRoleResponse>(
            `${AdminManagement.getAdminRoles}?${queryParams.toString()}`
        );

        return response.data;
    } catch (error) {
        console.error('Error fetching admin roles:', error);
        throw error;
    }
};

export const getAdminRolePermissions = async (
    adminRoles: Array<TGetUserRoleSchema>
): Promise<PermissionSchema[]> => {
    try {
        const permissionPromises = adminRoles.map((role) =>
            api
                .get<TGetAdminRolePermissionsResponse>(
                    `${AdminManagement.getRolePermissions}?role_id=${role.role_id}`
                )
                .then((response) => response.data)
        );

        const permissionsResults = await Promise.all(permissionPromises);
        let flattenedResults = permissionsResults.flatMap(
            (item) => item.role_permissions
        );
        return flattenedResults;
    } catch (error) {
        console.error('Error fetching admin role permissions:', error);
        throw error;
    }
};
export const getRolesOfUser = async (
    userId: string
): Promise<TGetRolesOfUserResponse> => {
    try {
        const response = await api.get<TGetRolesOfUserResponse>(
            `${AdminManagement.getRolesOfUser}?user_id=${userId}`
        );

        return response.data;
    } catch (error) {
        // throw transformErrorResponse(error);
    }
};

// ========================================
// Role-Permission Management Functions
// ========================================

/**
 * Assign a permission to a role
 * POST /admin/role-permissions
 */
export const assignRolePermission = async (
    payload: TAssignRolePermissionPayload
): Promise<TAssignRolePermissionResponse> => {
    try {
        const response = await api.post<TAssignRolePermissionResponse>(
            AdminManagement.assignRolePermission,
            payload
        );
        return response.data;
    } catch (error) {
        console.error('Error assigning role permission:', error);
        throw error;
    }
};

/**
 * Get permissions assigned to a role
 * GET /admin/role-permissions?role_id=xxx
 */
export const getRolePermissions = async (
    roleId: string
): Promise<TGetRolePermissionsResponse> => {
    try {
        const response = await api.get<TGetRolePermissionsResponse>(
            `${AdminManagement.getRolePermissions}?role_id=${roleId}`
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching role permissions:', error);
        throw error;
    }
};

/**
 * Modify a role-permission record
 * PATCH /admin/role-permissions/:id
 */
export const updateRolePermission = async (
    id: string,
    payload: TUpdateRolePermissionPayload
): Promise<TUpdateRolePermissionResponse> => {
    try {
        const response = await api.patch<TUpdateRolePermissionResponse>(
            AdminManagement.updateRolePermission(id),
            payload
        );
        return response.data;
    } catch (error) {
        console.error('Error updating role permission:', error);
        throw error;
    }
};

/**
 * Remove a role-permission record
 * DELETE /admin/role-permissions/:id
 */
export const removeRolePermission = async (id: string): Promise<void> => {
    try {
        await api.delete(AdminManagement.removeRolePermission(id));
    } catch (error) {
        console.error('Error removing role permission:', error);
        throw error;
    }
};

// ========================================
// Legacy Functions (for backward compatibility)
// ========================================

// export const updateAdminPermissions = (id, permissions) => {
//     return api.patch(`/admin/administrators/${id}/permissions`, {
//         permissions,
//     });
// };

// export const approveAdmin = (id) => {
//     return api.post(`/admin/administrators/${id}/approve`);
// };

// export const rejectAdmin = (id) => {
//     return api.post(`/admin/administrators/${id}/reject`);
// };
